import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, ArrowRight, CheckCircle2, ClipboardList, Loader2, TriangleAlert } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useBusinessAuth } from '../../context/BusinessAuthContext';

import FulfillmentGroupBadge from '../../components/orders/FulfillmentGroupBadge';
import OrderOperationsTable from '../../components/orders/OrderOperationsTable';
import OrdersMetricCard from '../../components/orders/OrdersMetricCard';
import OrdersWorkspaceHeader from '../../components/orders/OrdersWorkspaceHeader';
import { businessFulfillmentService } from '../../services/businessFulfillmentService';
import { orderPresentation, queueGroupCount } from '../../utils/ordersOperationsPresentation';

const INITIAL_SNAPSHOT = {
  items: [],
  counts: {},
  meta: { total: 0 },
  workflowHealth: null,
};

function refreshedAt(value) {
  if (!value) return 'Waiting for current fulfillment data';
  return `Last refreshed ${value.toLocaleTimeString('en-PH', { hour: '2-digit', minute: '2-digit' })}`;
}

export default function OrdersDashboard() {
  const auth = useBusinessAuth();
  const canReadDelivery = ['OWNER', 'SUPER_OWNER', 'ADMIN'].includes(String(auth.role || '').toUpperCase())
    || auth.hasPermission?.('delivery.dispatch.read.business');
  const navigate = useNavigate();
  const [snapshot, setSnapshot] = useState(INITIAL_SNAPSHOT);
  const [attention, setAttention] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);
  const requestRef = useRef(0);

  const load = useCallback(async ({ quiet = false } = {}) => {
    const requestId = ++requestRef.current;
    if (!quiet) setLoading(true);
    setError('');
    try {
      const [nextSnapshot, nextAttention] = await Promise.all([
        businessFulfillmentService.list({ stage: 'ALL', page: 1, limit: 8, sort: 'oldest' }),
        businessFulfillmentService.list({ stage: 'EXCEPTIONS', page: 1, limit: 4, sort: 'oldest' }),
      ]);
      if (requestId !== requestRef.current) return;
      setSnapshot(nextSnapshot || INITIAL_SNAPSHOT);
      setAttention(nextAttention?.items || []);
      setUpdatedAt(new Date());
    } catch {
      if (requestId === requestRef.current) {
        setError('Could not load the current Orders workload. Check your connection and try again.');
      }
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    return () => { requestRef.current += 1; };
  }, [load]);

  useEffect(() => {
    const onFocus = () => load({ quiet: true });
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  const metrics = useMemo(() => ({
    open: Number(snapshot.meta?.total || 0),
    prepare: queueGroupCount(snapshot.counts, 'TO_PREPARE'),
    ready: queueGroupCount(snapshot.counts, 'READY_FOR_HANDOFF'),
    attention: queueGroupCount(snapshot.counts, 'NEEDS_ATTENTION'),
  }), [snapshot]);

  const unhealthy = ['DEGRADED', 'UNAVAILABLE', 'UNKNOWN'].includes(snapshot.workflowHealth?.state);
  const openOrder = item => navigate(`/business/orders/fulfillment/${item.orderId}?group=${orderPresentation(item).group || 'TO_PREPARE'}`);

  return (
    <main className="min-w-0 space-y-5 text-neutral-950 dark:text-white">
      <OrdersWorkspaceHeader
        eyebrow="Logistics / Fulfillment"
        title="Orders overview"
        subtitle="A current operational view of preparation, rider handoff readiness, and fulfillment blockers."
        meta={refreshedAt(updatedAt)}
      >
        <div className="flex flex-wrap gap-2">{canReadDelivery && <Link to="/business/delivery" className="inline-flex min-h-10 items-center rounded-lg border border-neutral-300 px-4 py-2 text-sm font-semibold hover:bg-neutral-100 dark:border-neutral-700 dark:hover:bg-neutral-900">Logistics overview</Link>}<Link to="/business/orders/fulfillment" className="business-primary-action inline-flex min-h-10 items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-on-accent hover:bg-accent-hover active:bg-accent-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus">
          Open fulfillment <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link></div>
      </OrdersWorkspaceHeader>

      {error ? (
        <div role="alert" className="flex flex-col gap-3 rounded-xl border border-danger bg-danger-subtle p-4 text-sm text-danger sm:flex-row sm:items-center sm:justify-between">
          <span className="flex items-start gap-2"><AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />{error}</span>
          <button type="button" onClick={() => load()} className="min-h-10 self-start rounded-lg border border-current px-3 font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus sm:self-auto">Try again</button>
        </div>
      ) : null}

      {unhealthy ? (
        <div role="status" className="rounded-xl border border-warning bg-warning-subtle p-4 text-sm text-warning">
          <p className="font-semibold">Automatic handoff processing needs attention</p>
          <p className="mt-1">Ready parcels remain queued while delivery processing is unavailable. Review Delivery Operations for current worker status.</p>
        </div>
      ) : null}

      {!loading && !error && metrics.attention > 0 && <Link to="/business/orders/fulfillment?group=NEEDS_ATTENTION" className="flex min-h-16 items-center gap-3 rounded-xl border border-danger bg-danger-subtle p-4 text-danger hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"><TriangleAlert className="h-5 w-5 shrink-0" aria-hidden="true" /><span className="min-w-0 flex-1"><strong className="block">{metrics.attention} orders need fulfillment attention</strong><span className="text-sm">Review readiness blockers, dispatch holds, and shipment failures.</span></span><ArrowRight className="h-4 w-4 shrink-0" aria-hidden="true" /></Link>}

      <section aria-label="Orders workload" className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <OrdersMetricCard label="Open fulfillment" value={loading ? '—' : metrics.open} description="All orders still inside the fulfillment workspace." />
        <OrdersMetricCard label="To prepare" value={loading ? '—' : metrics.prepare} description="New, packing, or packed parcels awaiting readiness." tone="action" to="/business/orders/fulfillment?group=TO_PREPARE" />
        <OrdersMetricCard label="Ready for handoff" value={loading ? '—' : metrics.ready} description="Ready parcels waiting for rider pickup to begin." tone="ready" to="/business/orders/fulfillment?group=READY_FOR_HANDOFF" />
        <OrdersMetricCard label="Needs attention" value={loading ? '—' : metrics.attention} description="Readiness blockers, dispatch holds, or shipment failures." tone="danger" to="/business/orders/fulfillment?group=NEEDS_ATTENTION" />
      </section>

      <div className="grid min-w-0 gap-5 xl:grid-cols-[minmax(0,1fr)_20rem]">
        <section className="min-w-0 overflow-hidden rounded-panel border border-default bg-surface shadow-panel">
          <div className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="text-base font-semibold tracking-[-0.01em]">Oldest actionable orders</h2>
              <p className="mt-1 text-xs leading-5 text-neutral-500 dark:text-neutral-400">Ordered by current waiting age, oldest first.</p>
            </div>
            <Link to="/business/orders" className="inline-flex min-h-9 items-center gap-1 self-start rounded-lg px-2 text-xs font-semibold text-neutral-600 hover:bg-neutral-100 hover:text-neutral-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus dark:text-neutral-300 dark:hover:bg-neutral-900 dark:hover:text-white">
              All orders <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </Link>
          </div>
          {loading ? (
            <div role="status" className="grid min-h-64 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-accent motion-reduce:animate-none" aria-hidden="true" /><span className="sr-only">Loading Orders overview</span></div>
          ) : (
            <OrderOperationsTable
              items={snapshot.items || []}
              onOpen={openOrder}
              emptyTitle="No open fulfillment work"
              emptyDescription="Customer orders appear here while they need packing, readiness, or rider handoff work."
            />
          )}
        </section>

        <aside className="self-start overflow-hidden rounded-xl border border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-950" aria-labelledby="orders-attention-title">
          <div className="border-b border-neutral-200 px-4 py-4 dark:border-neutral-800">
            <div className="flex items-center gap-2">
              <TriangleAlert className="h-4 w-4 text-danger" aria-hidden="true" />
              <h2 id="orders-attention-title" className="font-semibold">Needs attention</h2>
            </div>
            <p className="mt-1 text-xs leading-5 text-neutral-500 dark:text-neutral-400">Current blockers that require review before handoff.</p>
          </div>
          {loading ? (
            <div className="grid min-h-44 place-items-center"><Loader2 className="h-5 w-5 animate-spin text-accent motion-reduce:animate-none" aria-hidden="true" /></div>
          ) : attention.length === 0 ? (
            <div className="p-5 text-center">
              <CheckCircle2 className="mx-auto h-7 w-7 text-success" aria-hidden="true" />
              <p className="mt-3 text-sm font-semibold">No blockers right now</p>
              <p className="mt-1 text-xs leading-5 text-neutral-500">Orders appear here when readiness is blocked, revoked, held, or failed.</p>
            </div>
          ) : (
            <div className="divide-y divide-neutral-200 dark:divide-neutral-800">
              {attention.map(item => (
                <button key={item.orderId} type="button" onClick={() => openOrder(item)} className="block w-full p-4 text-left hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus dark:hover:bg-neutral-900">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm font-semibold">{item.orderReference}</span>
                    <ClipboardList className="h-4 w-4 shrink-0 text-neutral-400" aria-hidden="true" />
                  </div>
                  <p className="mt-2 text-xs leading-5 text-neutral-600 dark:text-neutral-300">{item.blocker || 'Review the current fulfillment blocker.'}</p>
                  <div className="mt-3"><FulfillmentGroupBadge group="NEEDS_ATTENTION" /></div>
                </button>
              ))}
            </div>
          )}
        </aside>
      </div>
    </main>
  );
}
